import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import type { BlacklistSlice } from './blacklist-slice';
import { createBlacklistSlice } from './blacklist-slice';
import type { PreferencesSlice } from './preferences-slice';
import { createPreferencesSlice, CUSTOM_PROFILE_ID } from './preferences-slice';
import { useCustomTransfersStore } from './useCustomTransfersStore';
import { repairPersistedConfiguration } from '@lib/transfers/utils/solver-configuration';

type AITransfersStoreControls = {
  resetAITransfersStore: () => void;
};

export type AITransfersStore = BlacklistSlice &
  PreferencesSlice &
  AITransfersStoreControls;

const STORAGE_KEY = 'ffh-ai-transfers';

/**
 * Repairs the persisted preferences as they are read back from localStorage,
 * so the store, the profile panel and every request all see the same values.
 * Runs on every hydration rather than behind a version bump, so a slider
 * bound tightened in a later release corrects an older saved value without
 * anyone remembering to migrate. A custom configuration that cannot be
 * repaired reverts the member to the backend's default profile.
 */
export const mergePersistedAITransfersState = <TState extends AITransfersStore>(
  persistedState: unknown,
  currentState: TState
): TState => {
  const persisted = (persistedState ?? {}) as Partial<AITransfersStore>;
  const hasCustomConfiguration =
    persisted.customConfiguration !== undefined &&
    persisted.customConfiguration !== null;
  const customConfiguration = hasCustomConfiguration
    ? repairPersistedConfiguration(persisted.customConfiguration)
    : null;
  const isCustomSelectedWithoutConfiguration =
    persisted.selectedProfileId === CUSTOM_PROFILE_ID && !customConfiguration;

  return {
    ...currentState,
    ...persisted,
    customConfiguration,
    selectedProfileId: isCustomSelectedWithoutConfiguration
      ? null
      : persisted.selectedProfileId ?? currentState.selectedProfileId
  };
};

const safeBrowserStorage = (): Storage | undefined => {
  if (typeof window === 'undefined') return undefined;
  return window.localStorage;
};

export const useAITransfersStore = create<AITransfersStore>()(
  persist(
    (...args) => {
      const [, get] = args;
      return {
        ...createBlacklistSlice(...args),
        ...createPreferencesSlice(...args),
        resetAITransfersStore: () => {
          const state = get();
          state.clearBlacklist();
          useCustomTransfersStore.getState().resetCustomTransfersLocalStore();
        }
      };
    },
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => safeBrowserStorage() as Storage),
      skipHydration: typeof window === 'undefined',
      merge: mergePersistedAITransfersState
    }
  )
);
